import { countryOfOriginOptions } from '@/utils/constants';
import { ROUTES } from '@/utils/data/routes';
import { zodResolver } from '@hookform/resolvers/zod';
import { ContentCopyOutlined, InfoOutlined, RemoveRedEyeOutlined } from '@mui/icons-material';
import { Button, ButtonProps, IconButton, InputAdornment, Typography } from '@mui/material';
import { Box, Stack } from '@mui/system';
import { captureException } from '@sentry/react';
import { usePostRegistrationOperatorMutation } from 'api/queries/registration';
import { useState } from 'react';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CheckboxInputField,
  ClientType,
  ContactInfoSchemaType,
  ControlledField,
  FormControlGroup,
  LanguageOptions,
  PhoneInputField,
  SelectInputField,
  StyledTooltip,
  colors,
  contactInfoDtoSchema,
  removeEmptyStringKeys,
} from 'remote/global';
import { FormStep, OperatorRegistrationPayload } from 'utils/data/data-types';
import {
  AuthenticationSchemaType,
  CompanyContactDetailsSchemaType,
  CompanyDetailsSchemaType,
  authenticationSchema,
  companyContactDetailsSchema,
  companyDetailsSchema,
} from './types';

function AccountStepForm({
  onValid,
  defaultValues,
}: {
  onValid: SubmitHandler<ContactInfoSchemaType>;
  defaultValues?: ContactInfoSchemaType;
}) {
  const tInstance = useTranslation();
  const { t } = tInstance;
  const {
    handleSubmit,
    control,
    formState: { isSubmitting, isValidating, isLoading },
  } = useForm<ContactInfoSchemaType>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      ...defaultValues,
    },
    resolver: zodResolver(contactInfoDtoSchema),
  });

  return (
    <form onSubmit={handleSubmit(onValid)}>
      <Stack direction='column' spacing={1} width={['auto', '320px']}>
        <ControlledField
          control={control}
          name='firstName'
          fieldProps={{ InputProps: { autoComplete: 'given-name' } }}
        />
        <ControlledField
          control={control}
          name='lastName'
          fieldProps={{ InputProps: { autoComplete: 'family-name' } }}
        />
        <ControlledField
          control={control}
          name='email'
          translationKey='personalEmail'
          fieldProps={{
            InputProps: {
              type: 'email',
              autoComplete: 'email',
            },
          }}
        />
        <ControlledField
          control={control}
          name='phone'
          render={(renderProps) => (
            <PhoneInputField {...renderProps} translationKey='personalPhone' />
          )}
        />
      </Stack>
      <Button
        type='submit'
        disabled={isSubmitting || isValidating || isLoading}
        variant='contained'
        fullWidth
        sx={{ my: '2rem' }}
      >
        {t('continue')}
      </Button>
    </form>
  );
}

function InlineButton(props: ButtonProps) {
  const { children, ...rest } = props;
  return (
    <Button
      type='button'
      variant='text'
      sx={{
        p: 0,
        pt: 0.5,
        height: 'auto',
        fontSize: '13px',
        textDecoration: 'none',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        textAlign: 'left',
        gap: 0.5,
        justifyContent: 'flex-start',
        '&:focus,&:hover': {
          textDecoration: 'underline',
        },
      }}
      {...rest}
    >
      <ContentCopyOutlined color='primary' sx={{ fontSize: '13px' }} />
      {children}
    </Button>
  );
}

type CompanyDetailsStepFormProps = {
  onValid: SubmitHandler<CompanyDetailsSchemaType>;
  onBack: (data: CompanyDetailsSchemaType) => void;
  defaultValues: CompanyDetailsSchemaType | undefined;
};

const defaultCountryCode = {
  [LanguageOptions.en]: 'EE',
  [LanguageOptions.et]: 'EE',
  [LanguageOptions.ru]: 'EE',
  [LanguageOptions.lv]: 'LV',
};

function CompanyDetailsStepForm({ onValid, onBack, defaultValues }: CompanyDetailsStepFormProps) {
  const tInstance = useTranslation();
  const { t, i18n } = tInstance;
  const defaultCountry = defaultCountryCode[i18n.language as LanguageOptions];
  const {
    handleSubmit,
    control,
    getValues,
    formState: { isSubmitting, isValidating, isLoading },
  } = useForm<CompanyDetailsSchemaType>({
    defaultValues: {
      regCode: '',
      legalName: '',
      companyVat: '',
      address: '',
      operatingLicense: '',
      type: ClientType.BUSINESS,
      country: defaultCountry,
      ...defaultValues,
    },
    resolver: zodResolver(companyDetailsSchema),
  });

  return (
    <form onSubmit={handleSubmit(onValid)}>
      <Stack direction='column' spacing={1} width={['auto', '320px']} textAlign='left'>
        <ControlledField
          control={control}
          name='country'
          render={(renderProps) => (
            <SelectInputField {...renderProps} options={countryOfOriginOptions} />
          )}
          focusInitial
        />
        <ControlledField control={control} name='regCode' />
        <ControlledField control={control} name='legalName' />
        <ControlledField control={control} name='companyVat' />
        <ControlledField control={control} name='address' />
        <ControlledField control={control} name='operatingLicense' />
      </Stack>
      <Stack direction='row' justifyContent='space-between' spacing={1} my='2rem'>
        <Button
          type='button'
          onClick={() => onBack(getValues())}
          variant='outlined'
          disabled={isSubmitting || isValidating || isLoading}
        >
          {t('back')}
        </Button>
        <Button
          type='submit'
          disabled={isSubmitting || isValidating || isLoading}
          variant='contained'
          sx={{ display: 'flex', maxWidth: 300 }}
        >
          {t('continue')}
        </Button>
      </Stack>
    </form>
  );
}

type CompanyContactDetailsStepFormProps = {
  preset:
    | {
        email: string | undefined;
        phone: string | undefined;
      }
    | undefined;
  onValid: SubmitHandler<CompanyContactDetailsSchemaType>;
  onBack: (data: CompanyContactDetailsSchemaType) => void;
  defaultValues: CompanyContactDetailsSchemaType | undefined;
};

function CompanyContactDetailsStepForm({
  onValid,
  onBack,
  defaultValues,
  preset,
}: CompanyContactDetailsStepFormProps) {
  const tInstance = useTranslation();
  const { t } = tInstance;
  const {
    handleSubmit,
    control,
    getValues,
    setValue,
    formState: { isSubmitting, isValidating, isLoading },
  } = useForm<CompanyContactDetailsSchemaType>({
    defaultValues: {
      brandName: '',
      contactPhoneNumber: '',
      contactEmail: '',
      emergencyPhoneNumber: '',
      emergencyEmail: '',
      website: '',
      offerContactEmail: '',
      offerContactPhone: '',
      ...defaultValues,
    },
    resolver: zodResolver(companyContactDetailsSchema),
  });

  return (
    <form onSubmit={handleSubmit(onValid)}>
      <Stack direction='column' spacing={1} width={['auto', '320px']} textAlign='left'>
        <ControlledField control={control} name='brandName' focusInitial />
        <ControlledField
          control={control}
          name='contactPhoneNumber'
          render={(renderProps) => <PhoneInputField {...renderProps} />}
        />
        {preset?.phone != null && (
          <InlineButton onClick={() => setValue('contactPhoneNumber', preset.phone!)}>
            {t('SignUpView.useSamePhone')}
          </InlineButton>
        )}
        <ControlledField
          control={control}
          name='contactEmail'
          fieldProps={{
            InputProps: {
              type: 'email',
              autoComplete: 'email',
            },
          }}
        />
        {preset?.email != null && (
          <InlineButton onClick={() => setValue('contactEmail', preset.email!)}>
            {t('SignUpView.useSameEmail')}
          </InlineButton>
        )}
        <ControlledField
          control={control}
          name='emergencyPhoneNumber'
          render={(renderProps) => <PhoneInputField {...renderProps} />}
        />
        {preset?.phone != null && (
          <InlineButton onClick={() => setValue('emergencyPhoneNumber', preset.phone!)}>
            {t('SignUpView.useSamePhone')}
          </InlineButton>
        )}
        <ControlledField
          control={control}
          name='emergencyEmail'
          fieldProps={{
            InputProps: {
              type: 'email',
              autoComplete: 'email',
            },
          }}
        />
        {preset?.email != null && (
          <InlineButton onClick={() => setValue('emergencyEmail', preset.email!)}>
            {t('SignUpView.useSameEmail')}
          </InlineButton>
        )}
        <ControlledField
          control={control}
          name='offerContactPhone'
          render={(renderProps) => (
            <PhoneInputField
              {...renderProps}
              fieldProps={{
                label: (
                  <Box
                    component='span'
                    sx={{ verticalAlign: 'middle', display: 'flex', justifyContent: 'flex-start' }}
                  >
                    <Box>{t('FormFields.offerContactPhone.label').toString()}</Box>
                    <Box sx={{ '& .MuiButtonBase-root': { width: 'inherit' }, '& svg': { ml: 0 } }}>
                      <StyledTooltip title={t('FormFields.offerContactPhone.tooltip')}>
                        <IconButton size='small'>
                          <InfoOutlined
                            fontSize='small'
                            sx={{ fontSize: '16px', color: colors.gray3, mt: -0.3, ml: 5 }}
                          />
                        </IconButton>
                      </StyledTooltip>
                    </Box>
                  </Box>
                ),
              }}
            />
          )}
        />
        {preset?.phone != null && (
          <InlineButton onClick={() => setValue('offerContactPhone', preset.phone!)}>
            {t('SignUpView.useSameEmail')}
          </InlineButton>
        )}
        <ControlledField
          control={control}
          name='offerContactEmail'
          fieldProps={{
            label: (
              <Box component='span' sx={{ verticalAlign: 'middle' }}>
                {t('FormFields.offerContactEmail.label').toString()}{' '}
                <StyledTooltip title={t('FormFields.offerContactEmail.tooltip')}>
                  <IconButton size='small'>
                    <InfoOutlined
                      fontSize='small'
                      sx={{ fontSize: '16px', color: colors.gray3, mt: -0.3 }}
                    />
                  </IconButton>
                </StyledTooltip>
              </Box>
            ),
          }}
        />
        {preset?.email != null && (
          <InlineButton onClick={() => setValue('offerContactEmail', preset.email!)}>
            {t('SignUpView.useSameEmail')}
          </InlineButton>
        )}
        <ControlledField control={control} name='website' />
      </Stack>
      <Stack direction='row' justifyContent='space-between' spacing={1} my='2rem'>
        <Button
          type='button'
          onClick={() => onBack(getValues())}
          variant='outlined'
          disabled={isSubmitting || isValidating || isLoading}
        >
          {t('back')}
        </Button>
        <Button
          type='submit'
          disabled={isSubmitting || isValidating || isLoading}
          variant='contained'
          sx={{ display: 'flex', maxWidth: 300 }}
        >
          {t('continue')}
        </Button>
      </Stack>
    </form>
  );
}

function AuthStepForm({
  onValid,
  onBack,
  defaultValues,
}: {
  onValid: SubmitHandler<AuthenticationSchemaType>;
  onBack: (data: AuthenticationSchemaType) => void;
  defaultValues: AuthenticationSchemaType | undefined;
}) {
  const tInstance = useTranslation();
  const { t } = tInstance;
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const {
    handleSubmit,
    control,
    getValues,
    formState: { errors, isSubmitting, isValidating, isLoading },
  } = useForm<AuthenticationSchemaType>({
    defaultValues: {
      password: '',
      confirmPassword: '',
      termsAccepted: undefined,
      ...defaultValues,
    },
    resolver: zodResolver(authenticationSchema),
  });

  return (
    <form onSubmit={handleSubmit(onValid)}>
      <Stack direction='column' spacing={1} width={['auto', '320px']}>
        <ControlledField
          control={control}
          name='password'
          focusInitial
          fieldProps={{
            InputProps: {
              type: showPasswordFields ? 'text' : 'password',
              autoComplete: 'password',
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton
                    tabIndex={-1}
                    onMouseDown={() => setShowPasswordFields(true)}
                    onMouseUp={() => setShowPasswordFields(false)}
                    onMouseLeave={() => setShowPasswordFields(false)}
                    onTouchStart={() => setShowPasswordFields(true)}
                    onTouchEnd={() => setShowPasswordFields(false)}
                  >
                    <RemoveRedEyeOutlined />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
        <ControlledField
          control={control}
          name='confirmPassword'
          fieldProps={{
            InputProps: {
              type: showPasswordFields ? 'text' : 'password',
              autoComplete: 'new-password',
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton
                    tabIndex={-1}
                    onMouseDown={() => setShowPasswordFields(true)}
                    onMouseUp={() => setShowPasswordFields(false)}
                    onMouseLeave={() => setShowPasswordFields(false)}
                    onTouchStart={() => setShowPasswordFields(true)}
                    onTouchEnd={() => setShowPasswordFields(false)}
                  >
                    <RemoveRedEyeOutlined />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
        <FormControlGroup error={errors?.termsAccepted} required>
          <ControlledField
            control={control}
            name='termsAccepted'
            render={(renderProps) => <CheckboxInputField {...renderProps} />}
          />
        </FormControlGroup>
      </Stack>
      <Stack direction='row' justifyContent='space-between' spacing={1} my='2rem'>
        <Button
          type='button'
          onClick={() => onBack(getValues())}
          variant='outlined'
          disabled={isSubmitting || isValidating || isLoading}
        >
          {t('back')}
        </Button>
        <Button
          type='submit'
          disabled={isSubmitting || isValidating || isLoading}
          variant='contained'
          sx={{ display: 'flex', maxWidth: 300 }}
        >
          {t('actions.user.createAccount')}
        </Button>
      </Stack>
    </form>
  );
}

type FormResult = [
  ContactInfoSchemaType?,
  CompanyDetailsSchemaType?,
  CompanyContactDetailsSchemaType?,
  AuthenticationSchemaType?
];
type AccountRegistrationFormProps = {
  steps: FormStep[];
  activeStep: number;
  setActiveStep: (step: number) => void;
  defaultValues?: FormResult;
};

function AccountRegistrationForm({
  steps,
  activeStep,
  setActiveStep,
  defaultValues,
}: AccountRegistrationFormProps) {
  const { t, i18n } = useTranslation();
  const { accessToken } = useParams();
  const [formResult, setFormResult] = useState<FormResult | undefined>(defaultValues);
  const [contactInfoValues, companyDetailsValues, companyContactDetailsValues, authValues] =
    formResult ?? [];
  const navigate = useNavigate();
  const postRegistrationOperatorMutation = usePostRegistrationOperatorMutation();

  const contents = steps[activeStep];

  const setFormResultForStep = (step: number, data: any) => {
    setFormResult((prevState) => {
      const newState = prevState ?? [];
      newState[step] = data;
      return newState;
    });
  };

  const handleGoBack = (data: any) => {
    setFormResultForStep(activeStep, data);
    setActiveStep(activeStep - 1);
  };

  const handleValidStep = async (data: FieldValues) => {
    const nextStepIndex = activeStep + 1;

    // If is not the last step
    if (nextStepIndex < steps.length) {
      setFormResultForStep(activeStep, data);
      setActiveStep(nextStepIndex);
      return;
    }

    // Handle submitting the form
    const payload: OperatorRegistrationPayload = {
      contactInfo: contactInfoValues!,
      operator: {
        ...companyDetailsValues!,
        ...companyContactDetailsValues!,
      },
      lang: i18n.language,
      accessToken, // Needed when operator is preliminary
      ...authValues!, // Technically we dont need authValues and ...data, as they are the same
      ...data, // the data attribute has the newest values while authValues might have old values
    };

    // Removes all empty string values from the object
    const cleanPayload: OperatorRegistrationPayload = removeEmptyStringKeys(payload);

    try {
      await postRegistrationOperatorMutation.mutateAsync(cleanPayload);
      navigate(ROUTES.BaseRoutes.ROOT, { replace: true });
    } catch (err) {
      toast.error(t('Toast.failure'));
      captureException(err);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        justifyContent: 'center',
        mt: ['3rem', '6rem', '10rem'],
        maxWidth: '1500px',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, px: '1rem', maxWidth: '750px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography
            sx={{
              color: colors.uiBlue600,
              fontWeight: 700,
              textAlign: 'center',
              fontSize: '30px',
            }}
          >
            {contents.label}
          </Typography>
          <Typography
            sx={{
              color: colors.gray1,
              fontWeight: 400,
              textAlign: 'center',
              fontSize: '16px',
            }}
          >
            {contents.description}
          </Typography>
        </Box>
        {activeStep === 0 && (
          <AccountStepForm defaultValues={contactInfoValues} onValid={handleValidStep} />
        )}
        {activeStep === 1 && (
          <CompanyDetailsStepForm
            defaultValues={companyDetailsValues}
            onValid={handleValidStep}
            onBack={handleGoBack}
          />
        )}
        {activeStep === 2 && (
          <CompanyContactDetailsStepForm
            preset={{
              email: contactInfoValues?.email,
              phone: contactInfoValues?.phone,
            }}
            defaultValues={companyContactDetailsValues}
            onValid={handleValidStep}
            onBack={handleGoBack}
          />
        )}
        {activeStep === 3 && (
          <AuthStepForm
            defaultValues={authValues}
            onValid={handleValidStep}
            onBack={handleGoBack}
          />
        )}
      </Box>
    </Box>
  );
}

export default AccountRegistrationForm;
